import React, { Dispatch, FC, ReactNode, SetStateAction, createContext, useState } from 'react';

export interface AppliedFilterValue {
    uuid: string | null;
    name: string;
}

export interface AppliedFilter {
    label: string;
    values: AppliedFilterValue[];
}

type FiltersContextProps = {
    // filters
    appliedFilters: AppliedFilter[];
    setFilterValues: (filterName: string, values: AppliedFilterValue[]) => void;
    toggleFilterValue: (filterName: string, value: AppliedFilterValue, singleItem?: boolean) => void;
    removeFilter: (filterName: string) => void;
    removeAllFilters: () => void;
    // results
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    results: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setResults: Dispatch<SetStateAction<any[]>>;
    currentPage: number;
    setCurrentPage: (value: number) => void;
};

export const FiltersContext = createContext<FiltersContextProps>({
    // filters
    appliedFilters: [],
    setFilterValues: () => {},
    toggleFilterValue: () => {},
    removeFilter: () => {},
    removeAllFilters: () => {},
    // results
    results: [],
    setResults: () => {},
    currentPage: 1,
    setCurrentPage: () => {},
});

type FiltersContextProvider = {
    children: ReactNode;
};

export const FiltersProvider: FC<FiltersContextProvider> = ({ children }) => {
    // filters
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);

    const setFilterValues = (filterName: string, values: AppliedFilterValue[]) => {
        const currentLabelValues = appliedFilters.find((item) => item.label === filterName)?.values || [];

        if (currentLabelValues !== values) {
            values?.length > 0
                ? setAppliedFilters((previous) => {
                      const otherFilters = previous.filter((item) => item.label !== filterName);
                      return [...otherFilters, { label: filterName, values: values }];
                  })
                : setAppliedFilters((previous) => {
                      const otherFilters = previous.filter((item) => item.label !== filterName);
                      return [...otherFilters];
                  });
        }
    };

    const toggleFilterValue = (filterName: string, value: AppliedFilterValue, singleItem?: boolean) => {
        if (appliedFilters.map((item) => item.label).includes(filterName)) {
            const otherFilters = appliedFilters.filter((item) => item.label !== filterName);
            const currentLabelValues = appliedFilters.find((item) => item.label === filterName)?.values || [];

            if (currentLabelValues.includes(value)) {
                setAppliedFilters([
                    ...otherFilters,
                    { label: filterName, values: currentLabelValues.filter((item) => item !== value) },
                ]);
            } else if (singleItem) {
                setAppliedFilters([...otherFilters, { label: filterName, values: [value] }]);
            } else {
                setAppliedFilters([...otherFilters, { label: filterName, values: [...currentLabelValues, value] }]);
            }
        } else {
            setAppliedFilters([...appliedFilters, { label: filterName, values: [value] }]);
        }
    };

    const removeFilter = (filterName: string) => {
        const otherFilters = appliedFilters.filter((item) => item.label !== filterName);
        setAppliedFilters([...otherFilters]);
    };

    const removeAllFilters = () => {
        setAppliedFilters([]);
    };

    // results
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [results, setResults] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const contextValue: FiltersContextProps = {
        // filters
        appliedFilters,
        setFilterValues,
        toggleFilterValue,
        removeFilter,
        removeAllFilters,
        // results
        results,
        setResults,
        currentPage,
        setCurrentPage,
    };

    return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
};
